import TicketsAPI from 'dashboard/api/tickets';

function contractInvalidFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return (
    payload.contract_invalid === true ||
    payload.contractInvalid === true ||
    payload.contract_invalid === 'true' ||
    payload.contractInvalid === 'true'
  );
}

function parseTicketCreateErrorResponse(error) {
  const raw = error?.response?.data;
  if (raw == null) {
    return { payload: null, contractInvalid: false };
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return { payload: parsed, contractInvalid: contractInvalidFromPayload(parsed) };
    } catch {
      return { payload: { error: raw }, contractInvalid: false };
    }
  }
  return { payload: raw, contractInvalid: contractInvalidFromPayload(raw) };
}

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  meta: {
    count: 0,
    currentPage: 1,
    totalPages: 1,
  },
  stats: {
    openCount: 0,
    inProgressCount: 0,
    escalatedCount: 0,
    waitingClientCount: 0,
    pendingCount: 0,
    resolvedCount: 0,
    urgentCount: 0,
    overdueCount: 0,
    unassignedCount: 0,
  },
  trends: {
    total: { value: 0, isPositive: true },
    inProgress: { value: 0, isPositive: true },
    resolved: { value: 0, isPositive: true },
    urgent: { value: 0, isPositive: true },
  },
};

const getters = {
  getTickets: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getStats: $state => $state.stats,
  getTrends: $state => $state.trends,
  getTicketById: $state => id => {
    return $state.records.find(ticket => ticket.display_id === id);
  },
  getActiveTickets: $state => {
    return $state.records.filter(
      ticket => !['resolved', 'closed', 'cancelled'].includes(ticket.status)
    );
  },
};

const actions = {
  async fetchTickets({ commit }, params = {}) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await TicketsAPI.get(params);
      commit('setTickets', response.data.payload);
      commit('setMeta', response.data.meta);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTicket({ commit }, { id }) {
    commit('setUIFlag', { isFetchingItem: true });
    try {
      const response = await TicketsAPI.show(id);
      commit('addOrUpdateTicket', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetchingItem: false });
    }
  },

  async fetchMeta({ commit }) {
    const response = await TicketsAPI.meta();
    commit('setStats', response.data.meta);
    return response.data;
  },

  async createTicket({ commit }, ticketData) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await TicketsAPI.create(ticketData);
      commit('addOrUpdateTicket', response.data);
      return response.data;
    } catch (error) {
      const { payload, contractInvalid } = parseTicketCreateErrorResponse(error);
      const message =
        (typeof payload?.error === 'string' && payload.error) ||
        error.message ||
        'Request failed';
      const wrapped = new Error(message);
      wrapped.name = 'TicketCreateError';
      wrapped.contractInvalid = contractInvalid;
      wrapped.createTicketPayload = payload;
      wrapped.httpStatus = error.response?.status;
      throw wrapped;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateTicket({ commit }, { id, ...ticketData }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await TicketsAPI.update(id, ticketData);
      commit('addOrUpdateTicket', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteTicket({ commit }, id) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await TicketsAPI.delete(id);
      commit('removeTicket', id);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async assignTicket({ commit }, { id, assigneeId }) {
    const response = await TicketsAPI.assign(id, assigneeId);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async escalateTicket({ commit }, { id, escalateToId, notes }) {
    const response = await TicketsAPI.escalate(id, escalateToId, notes);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async resolveTicket({ commit }, { id, resolutionNotes }) {
    const response = await TicketsAPI.resolve(id, resolutionNotes);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async closeTicket({ commit }, id) {
    const response = await TicketsAPI.close(id);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async reopenTicket({ commit }, id) {
    const response = await TicketsAPI.reopen(id);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async verifyTicket({ commit }, { id }) {
    const response = await TicketsAPI.verify(id);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async rejectVerification({ commit }, { id, notes }) {
    const response = await TicketsAPI.rejectVerification(id, notes);
    commit('addOrUpdateTicket', response.data);
    return response.data;
  },

  async createFollowUpTicket({ commit }, { id, ...params }) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await TicketsAPI.createFollowUp(id, params);
      commit('addOrUpdateTicket', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async searchTickets({ commit }, query) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await TicketsAPI.search(query);
      commit('setTickets', response.data.payload);
      commit('setMeta', response.data.meta);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTicketsByContact(_, { contactId }) {
    const response = await TicketsAPI.get({ contact_id: contactId });
    return response.data.payload || [];
  },

  async fetchDashboardTickets({ commit }, params = {}) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await TicketsAPI.dashboard(params);
      commit('setTickets', response.data.payload);
      commit('setMeta', { count: response.data.meta.count });
      if (response.data.trends) {
        commit('setTrends', response.data.trends);
      }
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },
};

const mutations = {
  setTickets($state, tickets) {
    $state.records = tickets;
  },

  setMeta($state, meta) {
    $state.meta = {
      count: meta.count,
      currentPage: meta.current_page,
      totalPages: meta.total_pages,
    };
  },

  setStats($state, stats) {
    $state.stats = {
      openCount: stats.open_count,
      inProgressCount: stats.in_progress_count,
      escalatedCount: stats.escalated_count,
      waitingClientCount: stats.waiting_client_count,
      pendingCount: stats.pending_count,
      resolvedCount: stats.resolved_count,
      urgentCount: stats.urgent_count,
      overdueCount: stats.overdue_count,
      unassignedCount: stats.unassigned_count,
    };
  },

  addOrUpdateTicket($state, ticket) {
    const index = $state.records.findIndex(t => t.id === ticket.id);
    if (index !== -1) {
      $state.records.splice(index, 1, ticket);
    } else {
      $state.records.push(ticket);
    }
  },

  removeTicket($state, id) {
    $state.records = $state.records.filter(ticket => ticket.id !== id);
  },

  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },

  setTrends($state, trends) {
    $state.trends = {
      total: {
        value: trends.total.value,
        isPositive: trends.total.is_positive,
      },
      inProgress: {
        value: trends.in_progress.value,
        isPositive: trends.in_progress.is_positive,
      },
      resolved: {
        value: trends.resolved.value,
        isPositive: trends.resolved.is_positive,
      },
      urgent: {
        value: trends.urgent.value,
        isPositive: trends.urgent.is_positive,
      },
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
